import { useRef, useState } from "react";
import type { WebSocketMessage } from "../types";
import { MessageBubble } from "./MessageBubble";

interface ChatProps {
  messages: WebSocketMessage[];
  onSendMessage?: (message: string) => void;
  disabled?: boolean;
  memberEmail: string;
  sourceEntity: string;
}

export function Chat({
  messages,
  onSendMessage,
  disabled = false,
  memberEmail,
  sourceEntity,
}: ChatProps) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isDarkMode = false; // Can be made dynamic later

  const handleSend = () => {
    if (inputValue.trim() && onSendMessage && !disabled) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="Chat grid grid-rows-[1fr_auto] h-full">
      {/* Messages container */}
      <div className="overflow-y-auto p-6 space-y-4 min-h-0">
        {messages.map((message) => (
          <MessageBubble
            key={message.uid}
            message={message}
            memberEmail={memberEmail}
            sourceEntity={sourceEntity}
          />
        ))}
      </div>

      {/* Input bar */}
      <footer
        className={[
          "grid",
          "grid-cols-[1fr_auto]",
          "gap-2",
          "md:gap-3",
          "items-center",
          "p-2",
          "md:p-4",
          "border-t",
          "min-w-0",
          "relative",
          "z-10",
          isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white",
        ].join(" ")}
      >
        <div className="grid grid-cols-[1fr] gap-2 items-center min-w-0">
          <input
            ref={inputRef}
            type="text"
            placeholder={disabled ? "Connecting..." : "type your message..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={[
              "w-full",
              "min-w-0",
              "px-2",
              "md:px-4",
              "py-2",
              "border",
              "rounded-lg",
              "focus:outline-none",
              "focus:ring-2",
              isDarkMode
                ? [
                    "border-gray-600",
                    "bg-gray-700",
                    "text-gray-100!",
                    "placeholder-gray-400",
                    "focus:ring-blue-400",
                  ].join(" ")
                : [
                    "border-gray-300",
                    "bg-white",
                    "text-gray-900",
                    "placeholder-gray-500",
                    "focus:ring-blue-500",
                  ].join(" "),
            ].join(" ")}
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSend();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSend();
          }}
          disabled={!inputValue.trim() || disabled}
          className={[
            "w-10",
            "h-10",
            "md:w-10",
            "md:h-10",
            "rounded-lg!",
            "grid",
            "place-items-center",
            "select-none",
            "relative",
            "z-10",
            isDarkMode
              ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
              : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700",
            !inputValue.trim() ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          ].join(" ")}
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
        >
          <span className="text-white text-xl">→</span>
        </button>
      </footer>
    </div>
  );
}
